import { defineStore } from 'pinia'
import { api } from '~/api/apis'
import type { AccountData } from '~/api/schemas/account-data'
import type { RoleData } from '~/api/schemas/role-data'
import { AccountStatus } from '~/enums/account-status-code'
import { useAuthStore } from './auth'

/** 使用者 */
export const useUserStore = defineStore('user', () => {
  /** 使用者 */
  const user = ref<AccountData | null>(null)
  /** 角色列表 */
  const roleList = ref<RoleData[]>([])

  const adminParameterdStore = useAdminParameterdStore()

  const { accountNo } = storeToRefs(useAuthStore())
  const { tpEmailList } = storeToRefs(useAppParameterdStore())
  const { accountStatusList } = storeToRefs(adminParameterdStore)

  /** 使用者姓名 */
  const userRealName = computed(() => user.value?.realName ?? '')
  /** 使用者角色列表 */
  const userRoles = computed((): string[] =>
    user.value && Array.isArray(user.value.role) ? user.value.role : []
  )

  /** 是否為系統管理員 tpAdmin */
  const isTpAdmin = computed(() => userRoles.value.includes('tpAdmin'))
  /** 是否為 TP 帳號 */
  const isTpAccount = computed(
    () =>
      !!tpEmailList.value.find(
        ({ pdCode }) => pdCode && accountNo.value.endsWith(pdCode)
      )
  )

  const isSales = computed(() =>
    userRoles.value.some((role) =>
      [
        'tpAdmin',
        'rscDigiMars',
        'rscDigiRunner',
        'rscCHAT',
        'rscDigiLogs',
        'rscRPA'
      ].includes(role)
    )
  )

  const isBusinessStaff = computed(() =>
    userRoles.value.some((role) => ['tpBusinessStaff'].includes(role))
  )

  const isBusinessSupervisor = computed(() =>
    userRoles.value.some((role) => ['tpBusinessSupervisor'].includes(role))
  )

  const isOperationsManager = computed(() =>
    userRoles.value.some((role) => ['tpOperationsManager'].includes(role))
  )

  // const isDirector = computed(() =>
  //   userRoles.value.some((role) => ['tpDirector'].includes(role))
  // )

  const accountRole = computed(() => userRoles.value)

  const userPicture = computed(() => user.value?.picture ?? '')

  const canAccessAdmin = computed(() => roleMenu.value.length > 0)

  const roleMenu = computed(() => {
    const result: string[] = []

    if (
      userRoles.value &&
      toBackendRoles.value &&
      typeof userRoles.value !== 'string'
    ) {
      userRoles.value.forEach((role) => {
        const roleData = toBackendRoles.value.find((r) => r.roleCode === role)

        if (roleData) {
          roleData.roleMenu.forEach((item) => {
            if (!result.includes(item)) {
              result.push(item)
            }
          })
        }
      })
    }
    return result
  })

  const toBackendRoles = computed(() => {
    const roleListValue = roleList.value
    return roleListValue ? roleListValue.filter((role) => role.toBackend) : []
  })

  const userApproves = computed(() => {
    let approves: string[] = []

    if (isTpAdmin.value) {
      approves = [
        AccountStatus.PENDING,
        AccountStatus.SUPERVISOR_APPROVED,
        AccountStatus.SUPERVISOR_UNAPPROVED,
        AccountStatus.MANAGER_UNAPPROVED,
        AccountStatus.PENDING_EMPLOYEE,
        AccountStatus.MANAGER_UNAPPROVED_EMPLOYEE,
        AccountStatus.APPROVED
      ]
    } else if (isOperationsManager.value) {
      approves = [
        AccountStatus.SUPERVISOR_APPROVED,
        AccountStatus.MANAGER_UNAPPROVED,
        AccountStatus.PENDING_EMPLOYEE,
        AccountStatus.MANAGER_UNAPPROVED_EMPLOYEE,
        AccountStatus.APPROVED
      ]
    } else if (isBusinessSupervisor.value || isBusinessStaff.value) {
      approves = [
        AccountStatus.PENDING,
        AccountStatus.SUPERVISOR_APPROVED,
        AccountStatus.SUPERVISOR_UNAPPROVED,
        AccountStatus.MANAGER_UNAPPROVED,
        AccountStatus.APPROVED
      ]
    }
    // else if (isDirector.value) {
    //   approves = [
    //     AccountStatus.PENDING_EMPLOYEE,
    //     AccountStatus.MANAGER_UNAPPROVED_EMPLOYEE,
    //     AccountStatus.APPROVED
    //   ]
    // }

    return accountStatusList.value.filter(
      (approve) => approve.pdCode && approves.includes(approve.pdCode)
    )
  })

  const userApproveCodes = computed(() =>
    userApproves.value.map((approve) => approve.pdCode)
  )

  const userRolesEntities = computed(() => {
    const roleListValue = roleList.value
    return roleListValue
      ? roleListValue.filter((role) => userRoles.value.includes(role.roleCode))
      : []
  })

  const userRolesToString = computed(() =>
    Array.isArray(userRoles.value) ? userRoles.value.join(',') : ''
  )

  /** 設定使用者 */
  const setUser = (value: AccountData | null) => {
    user.value = value
  }

  /** 設定角色列表 */
  const setRoleList = (value: RoleData[]) => {
    roleList.value = value
  }

  /** 取得使用者 */
  const getUser = async () => {
    const getAccountResult = await useAppApi(
      api.getAccount(accountNo.value)
    ).call()

    if (getAccountResult.success) {
      setUser(getAccountResult.response.data)
    }
  }

  /** 取得角色列表 */
  const getRoleList = async () => {
    const getRoleListResult = await useAppApi(api.getRoleList()).call()

    if (getRoleListResult.success) {
      setRoleList(getRoleListResult.response.data ?? [])
      return getRoleListResult.response.data
    }

    return []
  }

  return {
    user,
    roleList,
    userRealName,
    accountNo,
    userRoles,
    isTpAdmin,
    isTpAccount,
    isSales,
    isBusinessStaff,
    isBusinessSupervisor,
    isOperationsManager,
    // isDirector,
    accountRole,
    userPicture,
    canAccessAdmin,
    roleMenu,
    toBackendRoles,
    userApproves,
    userApproveCodes,
    userRolesEntities,
    userRolesToString,
    setUser,
    setRoleList,
    getUser,
    getRoleList
  }
})
